import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { searchProducts } from '../services/api';

export default function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState('');
  const [data, setData] = useState({ suggestions: [], products: [] });

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    let live = true;
    (async () => {
      const res = await searchProducts(q);
      if (live) setData(res);
    })();
    return () => { live = false; };
  }, [q]);

  const highlight = (text) => {
    if (!q) return text;
    const i = text.toLowerCase().indexOf(q.toLowerCase());
    if (i === -1) return text;
    return (
      <>
        {text.slice(0, i)}
        <b className="text-neutral-900">{text.slice(i, i + q.length)}</b>
        {text.slice(i + q.length)}
      </>
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute top-0 left-0 right-0 bg-white shadow-xl max-h-[92vh] overflow-y-auto animate-[fadeIn_0.2s_ease-out]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <label className="absolute -top-2 left-3 bg-white px-1 text-xs font-medium text-neutral-600">Search</label>
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search resin art, tables, clocks..."
                className="w-full border border-neutral-400 rounded-md px-4 py-3 pr-20 text-neutral-900 focus:outline-none focus:border-[#1e3a2b]"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {q && (
                  <button onClick={() => setQ('')} className="p-1.5 text-neutral-500 hover:text-neutral-800" aria-label="Clear">
                    <X className="w-4 h-4" />
                  </button>
                )}
                <button className="p-1.5 text-neutral-700" aria-label="Search">
                  <Search className="w-4 h-4" />
                </button>
              </div>
            </div>
            <button onClick={onClose} className="lg:hidden p-2 text-neutral-600 hover:text-neutral-900" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Suggestions */}
          <div className="mt-6">
            <div className="text-xs font-semibold tracking-widest text-neutral-500 pb-2 border-b border-neutral-200">SUGGESTIONS</div>
            <ul className="mt-3 space-y-2">
              {data.suggestions.map((s, i) => (
                <li key={i}>
                  <button
                    onClick={() => setQ(s)}
                    className="text-neutral-700 hover:text-[#1e3a2b] text-sm sm:text-base transition-colors"
                  >
                    {highlight(s)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          {data.products.length > 0 && (
            <div className="mt-8">
              <div className="text-xs font-semibold tracking-widest text-neutral-500 pb-2 border-b border-neutral-200">PRODUCTS</div>
              <ul className="mt-3 divide-y divide-neutral-100">
                {data.products.map(p => (
                  <li key={p.id}>
                    <Link
                      to={`/product/${p.slug}`}
                      onClick={onClose}
                      className="flex items-center gap-3 py-3 hover:bg-neutral-50 rounded-md px-2 -mx-2 transition-colors"
                    >
                      <img src={p.image} alt="" className="w-14 h-14 rounded-md object-cover shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm sm:text-base font-medium text-neutral-900 line-clamp-1">{p.title.split('|')[0]}</div>
                        <div className="mt-0.5 text-sm">
                          <span className="font-semibold text-neutral-900">Rs. {p.price.toLocaleString('en-IN')}.00</span>{' '}
                          <span className="text-neutral-500 line-through">Rs. {p.originalPrice.toLocaleString('en-IN')}.00</span>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {q && data.products.length === 0 && (
            <div className="mt-8 text-sm text-neutral-500 text-center py-8">
              No products matched “{q}”. Try a different keyword.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
